/**
 * "Datenqualität & Hinweise" — what the figures above cannot say themselves.
 *
 * COUNTERS, NOT A SCORE
 * ---------------------
 * Every line is a real counter from a response, with the number the backend
 * sent. There is no completeness percentage and no traffic light, because
 * neither would be derivable: the backend counts specific contradictions, not
 * an overall health, and a "94 % vollständig" computed from ten unrelated
 * counters would be a number nobody could act on or check.
 *
 * ZERO IS NOT A FINDING
 * ---------------------
 * A counter at zero is left out. Listing ten satisfied checks would bury the one
 * that is not, and "0 unbekannte Kautionsarten" is not information an operator
 * needs. When every counter is zero the card says so in one line — but it says
 * "keine offenen Hinweise", not "vollständig", which would claim something about
 * data this endpoint never inspected.
 *
 * Severity is stated in words as well as colour: each group's rows carry their
 * level as a label, so the card is readable without distinguishing amber from
 * rose.
 */
import React from "react";
import { AlertTriangle, CheckCircle2, Info } from "lucide-react";

import { Skeleton } from "../../../components/ui/skeleton";
import {
  LANDLORD_QUALITY_ROWS,
  TENANT_QUALITY_ROWS,
  selectQualityFindings,
} from "./depositsFilters";
import { SEVERITY_LABELS, SEVERITY_ORDER } from "./depositsLabels";

const SEVERITY_STYLE = {
  bad: {
    icon: AlertTriangle,
    className: "text-rose-700 dark:text-rose-300",
  },
  warn: {
    icon: AlertTriangle,
    className: "text-amber-700 dark:text-amber-300",
  },
  info: {
    icon: Info,
    className: "text-sky-700 dark:text-sky-300",
  },
};

function sortBySeverity(findings) {
  return findings
    .slice()
    .sort(
      (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
    );
}

function FindingRow({ finding }) {
  const style = SEVERITY_STYLE[finding.severity] || SEVERITY_STYLE.info;
  const Icon = style.icon;
  return (
    <li
      data-testid={`deposits-quality-${finding.key}`}
      className="flex items-start gap-2 py-1.5"
    >
      <Icon aria-hidden className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${style.className}`} />
      <span className="min-w-0 text-[12px] leading-snug text-foreground">
        <span className="font-semibold tabular-nums">{finding.count}</span>{" "}
        {finding.label}
        {/* The level in words, so colour is never the only carrier. */}
        <span className="ml-1 text-[11px] text-muted-foreground">
          ({SEVERITY_LABELS[finding.severity]})
        </span>
      </span>
    </li>
  );
}

function Group({ title, findings, unavailable, testId }) {
  return (
    <div data-testid={testId}>
      <h3 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
        {title}
      </h3>
      {unavailable ? (
        <p className="mt-1 text-[12px] text-muted-foreground">Nicht verfügbar</p>
      ) : findings.length === 0 ? (
        <p className="mt-1 text-[12px] text-muted-foreground">Keine offenen Hinweise</p>
      ) : (
        <ul className="m-0 mt-1 list-none divide-y divide-border/60 p-0">
          {sortBySeverity(findings).map((f) => (
            <FindingRow key={f.key} finding={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DepositsDataQualityCard({
  tenantQuality,
  landlordQuality,
  tenantUnavailable = false,
  landlordUnavailable = false,
  loading = false,
}) {
  const tenantFindings = selectQualityFindings(tenantQuality, TENANT_QUALITY_ROWS);
  const landlordFindings = selectQualityFindings(landlordQuality, LANDLORD_QUALITY_ROWS);
  const nothingOpen =
    !tenantUnavailable &&
    !landlordUnavailable &&
    tenantFindings.length === 0 &&
    landlordFindings.length === 0;

  return (
    <section
      data-testid="deposits-quality-card"
      aria-busy={loading || undefined}
      className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2 className="text-[14px] font-semibold leading-tight text-card-foreground">
        Datenqualität &amp; Hinweise
      </h2>

      {loading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      ) : nothingOpen ? (
        <p
          data-testid="deposits-quality-empty"
          className="m-0 flex items-start gap-2 text-[12px] text-muted-foreground"
        >
          <CheckCircle2
            aria-hidden
            className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400"
          />
          Keine offenen Datenhinweise
        </p>
      ) : (
        <>
          <Group
            testId="deposits-quality-tenant"
            title="Mieterkautionen"
            findings={tenantFindings}
            unavailable={tenantUnavailable}
          />
          <Group
            testId="deposits-quality-landlord"
            title="Vermieter-Sicherheiten"
            findings={landlordFindings}
            unavailable={landlordUnavailable}
          />
        </>
      )}
    </section>
  );
}
